import { useState, useEffect } from "react";
import axios from "axios";
import SupplierLedgerDialog from "@/components/admin/SupplierLedgerDialog";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Plus,
  Loader2,
  Truck,
  Mail,
  Phone,
  Link,
  Copy,
  ExternalLink,
  CheckCircle,
  Clock,
  Search,
  Package,
  Tag,
  MapPin,
  Globe,
  Key,
  Edit,
  Banknote,
  Trash2,
  Table,
  LayoutGrid,
  MessageSquare,
  Send,
  Megaphone,
  Eye,
  CheckCircle2,
  ShieldAlert,
  Shield,
  FileText,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import SupplierCategoryPicker from "@/components/admin/SupplierCategoryPicker";
import { labelForCategory } from "@/data/supplierCategories";

const LEGACY_CATEGORIES_REMOVED = true;

export default function SupplierManagement() {
  const { token } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "table" or "grid"
  
  // Modal states
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [ledgerSupplier, setLedgerSupplier] = useState(null);
  const [showSetupLink, setShowSetupLink] = useState(false);
  const [showEditSupplier, setShowEditSupplier] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Send-option toggles for the Add Supplier modal
  const [openInOutlook, setOpenInOutlook] = useState(false);
  // Manual-add toggle: skip the activation email entirely. Used when admin is
  // adding a supplier we already work with offline.
  const [skipSetupEmail, setSkipSetupEmail] = useState(false);
  
  // Form states
  const [supplierForm, setSupplierForm] = useState({
    name: "",
    email: "",
    contact_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    website: "",
    credentials: "",
    categories: []
  });
  
  const [setupLinkData, setSetupLinkData] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  
  // Email states
  const [emailSupplier, setEmailSupplier] = useState(null);
  const [supplierProfile, setSupplierProfile] = useState(null);
  const [showSupplierProfile, setShowSupplierProfile] = useState(false);
  const [supplierFullProfile, setSupplierFullProfile] = useState(null);
  const [supplierProfileLoading, setSupplierProfileLoading] = useState(false);
  const [verifyingSupplier, setVerifyingSupplier] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await fetch(`${API}/suppliers`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data.suppliers || data || []);
      }
    } catch (err) {
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = async () => {
    if (!supplierForm.name || !supplierForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/suppliers`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ ...supplierForm, skip_setup_email: skipSetupEmail })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSetupLinkData(data);
        setShowAddSupplier(false);
        setShowSetupLink(true);
        const capturedForm = { ...supplierForm };
        const wantOutlook = openInOutlook;
        const wasManual = skipSetupEmail;
        resetForm();
        setSkipSetupEmail(false);
        fetchSuppliers();
        // Show appropriate message based on email status
        if (wasManual) {
          toast.success(`${capturedForm.name || "Supplier"} added & approved manually — no email sent.`);
        } else if (data.email_sent) {
          toast.success(`Setup email sent to ${capturedForm.email}!`);
        } else {
          toast.success("Supplier added! Setup link generated (email may be pending).");
        }
        // Optionally also open the user's Outlook / default mail client with
        // a pre-filled draft so the admin can review/personalize before sending
        // from their own mailbox (george.broughton@sterilemate.com).
        if (!wasManual && wantOutlook && data.setup_link) {
          openSupplierInvitationInOutlook({
            contactName: capturedForm.contact_name || capturedForm.name,
            companyName: capturedForm.name,
            recipientEmail: capturedForm.email,
            setupLink: data.setup_link,
          });
        }
      } else {
        const err = await res.json();
        if (err.detail?.includes("already exists")) {
          toast.error("A supplier with this email already exists. Please use a different email.");
        } else {
          toast.error(err.detail || "Failed to add supplier");
        }
      }
    } catch (err) {
      console.error("Add supplier error:", err);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Build a mailto: link with the supplier invitation copy and trigger the
  // admin's default mail client (Outlook desktop, Outlook on the web via
  // protocol handler, or Apple Mail). Used as an "also send via Outlook"
  // option from the Add Supplier modal.
  const openSupplierInvitationInOutlook = ({ contactName, companyName, recipientEmail, setupLink }) => {
    const subject = "Invitation: Join the AskIsaac Supplier Network via Sterile Mate";
    const body = [
      `Hi ${contactName || "there"},`,
      ``,
      `Welcome to AskIsaac, the official technology platform for medical device sourcing developed by Sterile Mate.`,
      ``,
      `Through AskIsaac, we provide our healthcare partners with a real-time bridge to verified suppliers. When a hospital partner requests an item, the platform alerts you instantly. To participate, simply submit your quote and lead times through the portal, and AskIsaac automatically delivers your proposal directly to the healthcare facility.`,
      ``,
      `${companyName || "Your team"} has been invited to join as a verified supplier partner. To complete your account setup and begin receiving quote requests, please use the secure link below:`,
      ``,
      `Complete Your Account Setup:`,
      setupLink,
      ``,
      `What to Expect:`,
      `  - Secure Access: Set your password and review our terms.`,
      `  - Real-Time Alerts: Connect your Telegram to @askisaac_bot for instant RFQ notifications.`,
      `  - Direct Sourcing: Start receiving and responding to hospital RFQs immediately.`,
      ``,
      `Note: This invitation link will expire in 7 days.`,
      ``,
      `We look forward to working with you to streamline healthcare infrastructure.`,
      ``,
      `Welcome aboard,`,
      ``,
      `—`,
      `REVOLUTIONIZING HYGIENE IN HEALTHCARE`,
      ``,
      `George Broughton III`,
      `President — Sterile Mate LLC`,
      `396 W Greens Road Suite 1139`,
      `Houston, TX 77067`,
      `Phone: 800.988.3892   Direct: 281.456.3458   Mobile: 323.243.9139`,
      `Email: george.broughton@sterilemate.com`,
      `Book a free 15-min Healthcare Revenue-Gap Audit: https://www.sterilemate.com/audit`,
    ].join("\r\n");
    const mailto = `mailto:${encodeURIComponent(recipientEmail || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const resetForm = () => {
    setSupplierForm({
      name: "",
      email: "",
      contact_name: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      website: "",
      credentials: "",
      categories: []
    });
  };

  const handleEditSupplier = async () => {
    if (!supplierForm.name || !supplierForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/backorders/suppliers/${editingSupplier.id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(supplierForm)
      });
      
      if (res.ok) {
        toast.success("Supplier updated successfully");
        setShowEditSupplier(false);
        setEditingSupplier(null);
        resetForm();
        fetchSuppliers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update supplier");
      }
    } catch (err) {
      toast.error("Failed to update supplier");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name || "",
      email: supplier.email || "",
      contact_name: supplier.contact_name || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      city: supplier.city || "",
      state: supplier.state || "",
      zip_code: supplier.zip_code || "",
      website: supplier.website || "",
      credentials: supplier.credentials || "",
      categories: supplier.categories || []
    });
    setShowEditSupplier(true);
  };

  const generateSetupLink = async (supplier) => {
    setSelectedSupplier(supplier);
    setGeneratingLink(true);
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplier.id}/generate-setup-link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSetupLinkData(data);
        setShowSetupLink(true);
        toast.success("Setup link generated!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to generate setup link");
      }
    } catch (err) {
      toast.error("Failed to generate setup link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const copySetupLink = () => {
    if (setupLinkData?.setup_link) {
      navigator.clipboard.writeText(setupLinkData.setup_link);
      toast.success("Link copied to clipboard!");
    }
  };

  const toggleCategory = (category) => {
    setSupplierForm(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  // Email functions
  const openEmailModal = (supplier) => {
    setEmailSupplier(supplier);
    setEmailSubject("");
    setEmailBody("");
    setShowEmailModal(true);
  };

  const handleSendEmail = () => {
    // Pre-fills the admin's own email client (Outlook/Apple Mail/Gmail).
    // Until the Resend domain is verified, we delegate the actual delivery
    // to the admin's outbox.
    if (!emailSupplier || !emailSubject || !emailBody) {
      toast.error("Please fill in subject and message");
      return;
    }
    const url =
      `mailto:${emailSupplier.email}` +
      `?subject=${encodeURIComponent(emailSubject)}` +
      `&body=${encodeURIComponent(emailBody)}`;
    window.open(url, "_blank");
    toast.success(`Opened email to ${emailSupplier.email} in your mail client`);
    setShowEmailModal(false);
    setEmailSupplier(null);
    setEmailSubject("");
    setEmailBody("");
  };

  // Open the supplier activation email in the admin's own Outlook
  const openSupplierActivationEmail = async (supplier) => {
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplier.id}/activation-mailto`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        toast.error("Could not build activation email");
        return;
      }
      const data = await res.json();
      window.open(data.mailto_url, "_blank");
      toast.success(`Opened activation email for ${supplier.email} in your mail client`);
    } catch (e) { toast.error("Failed to open email"); }
  };

  const handleVerifySupplier = async (supplier) => {
    setVerifyingSupplier(true);
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplier.id}/verify`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`${supplier.name || "Supplier"} marked as Verified`);
        await fetchSupplierFullProfile(supplier.id);
        await fetchSuppliers();
      } else {
        toast.error("Failed to mark supplier verified");
      }
    } catch (e) { toast.error("Failed to mark supplier verified"); }
    finally { setVerifyingSupplier(false); }
  };

  const handleRevokeSupplierVerification = async (supplier) => {
    if (!window.confirm("Revoke this supplier's verified status?")) return;
    setVerifyingSupplier(true);
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplier.id}/unverify`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Verification revoked");
        await fetchSupplierFullProfile(supplier.id);
        await fetchSuppliers();
      } else { toast.error("Failed to revoke verification"); }
    } catch (e) { toast.error("Failed to revoke verification"); }
    finally { setVerifyingSupplier(false); }
  };

  const fetchSupplierFullProfile = async (supplierId) => {
    setSupplierProfileLoading(true);
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplierId}/full-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSupplierFullProfile(await res.json());
    } catch (e) { /* ignore */ }
    finally { setSupplierProfileLoading(false); }
  };

  const uploadSupplierDocument = async (supplierId, file, documentType, documentName = null) => {
    if (!supplierId || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", documentType);
    if (documentName) fd.append("document_name", documentName);
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplierId}/upload-document`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        toast.success("Document uploaded");
        await fetchSupplierFullProfile(supplierId);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Upload failed");
      }
    } catch (e) { toast.error("Upload failed"); }
  };

  const deleteSupplierDocument = async (supplierId, documentId) => {
    if (!supplierId || !documentId) return;
    if (!window.confirm("Delete this document?")) return;
    try {
      const res = await fetch(
        `${API}/admin/suppliers/${supplierId}/documents/${documentId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success("Document removed");
        await fetchSupplierFullProfile(supplierId);
      } else {
        toast.error("Delete failed");
      }
    } catch (e) { toast.error("Delete failed"); }
  };

  const openSupplierProfile = async (supplier) => {
    setSupplierProfile(supplier);
    setShowSupplierProfile(true);
    setSupplierFullProfile(null);
    await fetchSupplierFullProfile(supplier.id);
  };

  // Toggle Alert Campaign permission for a supplier
  const toggleAlertCampaign = async (supplier, enabled) => {
    try {
      const res = await fetch(`${API}/admin/suppliers/${supplier.id}/permissions`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ alert_campaign_enabled: enabled })
      });
      
      if (res.ok) {
        toast.success(`Alert Campaign ${enabled ? 'enabled' : 'disabled'} for ${supplier.name}`);
        // Update local state
        setSuppliers(prev => prev.map(s => 
          s.id === supplier.id ? { ...s, alert_campaign_enabled: enabled } : s
        ));
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update permission");
      }
    } catch (err) {
      toast.error("Failed to update permission");
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeSuppliers = filteredSuppliers.filter(s => s.password_hash || s.setup_completed_at);
  const pendingSuppliers = filteredSuppliers.filter(s => !s.password_hash && !s.setup_completed_at);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SupplierLedgerDialog
        open={!!ledgerSupplier}
        onClose={() => setLedgerSupplier(null)}
        supplierId={ledgerSupplier?.id}
        supplierName={ledgerSupplier?.name}
        token={token}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Supplier Management</h2>
          <p className="text-slate-500 text-sm">Manage suppliers and their portal access</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`${API}/api/admin/exports/suppliers.csv`}
            onClick={async (e) => {
              // Fetch as blob so we can pass the auth header — link download
              // path would send the browser request without the Bearer token.
              e.preventDefault();
              try {
                const r = await axios.get(`${API}/api/admin/exports/suppliers.csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `sterile_mate_suppliers_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error("supplier export failed", err);
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md ring-1 ring-slate-300 hover:ring-cyan-500 text-sm font-semibold text-slate-700 hover:text-cyan-700 bg-white"
            data-testid="export-suppliers-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
          <Button
            onClick={() => setShowAddSupplier(true)}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="add-supplier-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Supplier
          </Button>
        </div>
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="supplier-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "table" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("table")}
            data-testid="view-table-btn"
          >
            <Table className="w-4 h-4 mr-1" />
            Table
          </Button>
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
            data-testid="view-grid-btn"
          >
            <LayoutGrid className="w-4 h-4 mr-1" />
            Grid
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-suppliers">
            <Truck className="w-4 h-4 mr-2" />
            All ({filteredSuppliers.length})
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active-suppliers">
            <CheckCircle className="w-4 h-4 mr-2" />
            Active ({activeSuppliers.length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending-suppliers">
            <Clock className="w-4 h-4 mr-2" />
            Pending ({pendingSuppliers.length})
          </TabsTrigger>
        </TabsList>

        {/* All Suppliers Tab */}
        <TabsContent value="all" className="mt-4">
          {viewMode === "table" ? (
            <SupplierTable 
              suppliers={filteredSuppliers} 
              onGenerateLink={generateSetupLink}
              generatingLink={generatingLink}
              selectedSupplier={selectedSupplier}
              onEdit={openEditModal}
              onViewLedger={setLedgerSupplier}
              onEmail={openEmailModal}
              onToggleAlertCampaign={toggleAlertCampaign}
              onViewProfile={openSupplierProfile}
              onSendActivation={openSupplierActivationEmail}
            />
          ) : (
            <SupplierGrid 
              suppliers={filteredSuppliers} 
              onGenerateLink={generateSetupLink}
              generatingLink={generatingLink}
              selectedSupplier={selectedSupplier}
              onEdit={openEditModal}
              onViewLedger={setLedgerSupplier}
              onEmail={openEmailModal}
              onToggleAlertCampaign={toggleAlertCampaign}
              onViewProfile={openSupplierProfile}
              onSendActivation={openSupplierActivationEmail}
            />
          )}
        </TabsContent>

        {/* Active Suppliers Tab */}
        <TabsContent value="active" className="mt-4">
          {viewMode === "table" ? (
            <SupplierTable 
              suppliers={activeSuppliers} 
              onGenerateLink={generateSetupLink}
              generatingLink={generatingLink}
              selectedSupplier={selectedSupplier}
              onEdit={openEditModal}
              onViewLedger={setLedgerSupplier}
              onEmail={openEmailModal}
              onToggleAlertCampaign={toggleAlertCampaign}
              onViewProfile={openSupplierProfile}
              onSendActivation={openSupplierActivationEmail}
            />
          ) : (
            <SupplierGrid 
              suppliers={activeSuppliers} 
              onGenerateLink={generateSetupLink}
              generatingLink={generatingLink}
              selectedSupplier={selectedSupplier}
              onEdit={openEditModal}
              onViewLedger={setLedgerSupplier}
              onEmail={openEmailModal}
              onToggleAlertCampaign={toggleAlertCampaign}
              onViewProfile={openSupplierProfile}
              onSendActivation={openSupplierActivationEmail}
            />
          )}
        </TabsContent>

        {/* Pending Suppliers Tab */}
        <TabsContent value="pending" className="mt-4">
          {viewMode === "table" ? (
            <SupplierTable 
              suppliers={pendingSuppliers} 
              onGenerateLink={generateSetupLink}
              generatingLink={generatingLink}
              selectedSupplier={selectedSupplier}
              onEdit={openEditModal}
              onViewLedger={setLedgerSupplier}
              onEmail={openEmailModal}
              onToggleAlertCampaign={toggleAlertCampaign}
              onViewProfile={openSupplierProfile}
              onSendActivation={openSupplierActivationEmail}
            />
          ) : (
            <SupplierGrid 
              suppliers={pendingSuppliers} 
              onGenerateLink={generateSetupLink}
              generatingLink={generatingLink}
              selectedSupplier={selectedSupplier}
              onEdit={openEditModal}
              onViewLedger={setLedgerSupplier}
              onEmail={openEmailModal}
              onToggleAlertCampaign={toggleAlertCampaign}
              onViewProfile={openSupplierProfile}
              onSendActivation={openSupplierActivationEmail}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Add Supplier Modal */}
      <Dialog open={showAddSupplier} onOpenChange={setShowAddSupplier}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" />
              Add New Supplier
            </DialogTitle>
            <DialogDescription>
              Add a new supplier to your network. They will receive a setup link to create their account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Company Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({...supplierForm, name: e.target.value})}
                  placeholder="e.g. Medical Supplies Inc."
                  data-testid="supplier-name"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={supplierForm.website}
                  onChange={(e) => setSupplierForm({...supplierForm, website: e.target.value})}
                  placeholder="https://www.company.com"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm({...supplierForm, contact_name: e.target.value})}
                  placeholder="e.g. John Smith"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})}
                  placeholder="(800) 988-3892"
                />
              </div>
            </div>
            
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={supplierForm.email}
                onChange={(e) => setSupplierForm({...supplierForm, email: e.target.value})}
                placeholder="sales@company.com"
                data-testid="supplier-email"
              />
            </div>
            
            {/* Address */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address
              </Label>
              <div className="space-y-3">
                <Input
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})}
                  placeholder="Street Address"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    value={supplierForm.city}
                    onChange={(e) => setSupplierForm({...supplierForm, city: e.target.value})}
                    placeholder="City"
                  />
                  <Input
                    value={supplierForm.state}
                    onChange={(e) => setSupplierForm({...supplierForm, state: e.target.value})}
                    placeholder="State"
                    maxLength={2}
                  />
                  <Input
                    value={supplierForm.zip_code}
                    onChange={(e) => setSupplierForm({...supplierForm, zip_code: e.target.value})}
                    placeholder="ZIP Code"
                  />
                </div>
              </div>
            </div>
            
            {/* Credentials */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Account Credentials / Notes
              </Label>
              <Textarea
                value={supplierForm.credentials}
                onChange={(e) => setSupplierForm({...supplierForm, credentials: e.target.value})}
                placeholder="Login credentials, account numbers, or special notes..."
                rows={2}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Store vendor portal login info or account details for your reference.</p>
            </div>
            
            {/* Categories */}
            <div className="border-t pt-4">
              <Label>What they supply</Label>
              <p className="text-xs text-slate-500 mb-2 mt-1">
                Tag every product category this supplier carries. We use this to filter suppliers when broadcasting backorder RFQs.
              </p>
              <SupplierCategoryPicker
                value={supplierForm.categories}
                onChange={(next) => setSupplierForm((f) => ({ ...f, categories: next }))}
                testId="add-supplier-categories"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col gap-3 sm:flex-col sm:items-stretch">
            {/* Sending options */}
            <label className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={openInOutlook}
                onChange={(e) => setOpenInOutlook(e.target.checked)}
                disabled={skipSetupEmail}
                className="mt-0.5 accent-amber-600 cursor-pointer disabled:cursor-not-allowed"
                data-testid="supplier-outlook-toggle"
              />
              <span className={`text-xs leading-snug ${skipSetupEmail ? "text-slate-400" : "text-slate-700"}`}>
                <strong className={skipSetupEmail ? "text-slate-400" : "text-slate-900"}>Also open in Outlook</strong> — autosends through AskIsaac and ALSO pops a pre-filled Outlook draft so you can review or personally sign before it goes out.
              </span>
            </label>
            <label className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
              <input
                type="checkbox"
                checked={skipSetupEmail}
                onChange={(e) => setSkipSetupEmail(e.target.checked)}
                className="mt-0.5 accent-amber-600 cursor-pointer"
                data-testid="supplier-skip-email-toggle"
              />
              <span className="text-xs text-amber-900 leading-snug">
                <strong className="text-amber-950">Manual add — skip activation email</strong> — use this when you&apos;re onboarding a supplier you already work with offline. The account is created and auto-approved without sending them a setup email.
              </span>
            </label>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowAddSupplier(false); resetForm(); setSkipSetupEmail(false); }}>
                Cancel
              </Button>
              <Button
                onClick={handleAddSupplier}
                disabled={submitting}
                className={skipSetupEmail ? "bg-amber-700 hover:bg-amber-800" : "bg-amber-600 hover:bg-amber-700"}
                data-testid="submit-add-supplier"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {skipSetupEmail
                  ? "Add & Approve (No Email)"
                  : openInOutlook
                    ? "Send & Open Outlook"
                    : "Email Setup Link"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Supplier Modal */}
      <Dialog open={showEditSupplier} onOpenChange={setShowEditSupplier}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Edit Supplier
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Company Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({...supplierForm, name: e.target.value})}
                  placeholder="e.g. Medical Supplies Inc."
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={supplierForm.website}
                  onChange={(e) => setSupplierForm({...supplierForm, website: e.target.value})}
                  placeholder="https://www.company.com"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm({...supplierForm, contact_name: e.target.value})}
                  placeholder="e.g. John Smith"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})}
                  placeholder="(800) 988-3892"
                />
              </div>
            </div>
            
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={supplierForm.email}
                onChange={(e) => setSupplierForm({...supplierForm, email: e.target.value})}
                placeholder="sales@company.com"
              />
            </div>
            
            {/* Address */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address
              </Label>
              <div className="space-y-3">
                <Input
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})}
                  placeholder="Street Address"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    value={supplierForm.city}
                    onChange={(e) => setSupplierForm({...supplierForm, city: e.target.value})}
                    placeholder="City"
                  />
                  <Input
                    value={supplierForm.state}
                    onChange={(e) => setSupplierForm({...supplierForm, state: e.target.value})}
                    placeholder="State"
                    maxLength={2}
                  />
                  <Input
                    value={supplierForm.zip_code}
                    onChange={(e) => setSupplierForm({...supplierForm, zip_code: e.target.value})}
                    placeholder="ZIP Code"
                  />
                </div>
              </div>
            </div>
            
            {/* Credentials */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Account Credentials / Notes
              </Label>
              <Textarea
                value={supplierForm.credentials}
                onChange={(e) => setSupplierForm({...supplierForm, credentials: e.target.value})}
                placeholder="Login credentials, account numbers, or special notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            {/* Categories */}
            <div className="border-t pt-4">
              <Label>What they supply</Label>
              <p className="text-xs text-slate-500 mb-2 mt-1">
                Tag every product category this supplier carries — used to filter RFQ broadcasts.
              </p>
              <SupplierCategoryPicker
                value={supplierForm.categories}
                onChange={(next) => setSupplierForm((f) => ({ ...f, categories: next }))}
                testId="edit-supplier-categories"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditSupplier(false); setEditingSupplier(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleEditSupplier} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Link Modal */}
      <Dialog open={showSetupLink} onOpenChange={setShowSetupLink}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {setupLinkData?.email_sent ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Setup Email Sent!
                </>
              ) : (
                <>
                  <Link className="w-5 h-5 text-amber-500" />
                  Setup Link Generated
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {setupLinkData?.email_sent 
                ? `A setup email has been sent to ${setupLinkData?.supplier?.email}`
                : `Send this link to ${setupLinkData?.supplier?.name || setupLinkData?.supplier?.company_name} to complete their account setup.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {setupLinkData?.email_sent && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-emerald-700 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Setup invitation emailed to <strong>{setupLinkData?.supplier?.email}</strong>
                </p>
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-2">Supplier</p>
              <p className="font-semibold text-slate-900">{setupLinkData?.supplier?.name || setupLinkData?.supplier?.company_name}</p>
              <p className="text-sm text-slate-600">{setupLinkData?.supplier?.email}</p>
            </div>
            
            <div>
              <Label className="text-slate-600">Setup Link</Label>
              <div className="mt-1 flex gap-2">
                <Input 
                  value={setupLinkData?.setup_link || ""} 
                  readOnly 
                  className="bg-white font-mono text-xs"
                />
                <Button variant="outline" onClick={copySetupLink}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">This link expires in 7 days.</p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Click &quot;Send Setup Link&quot; to open your email client with a pre-written invite.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSetupLink(false)}>Close</Button>
            <Button 
              variant="outline"
              onClick={() => window.open(setupLinkData?.setup_link, '_blank')} 
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Preview Link
            </Button>
            <Button 
              onClick={() => {
                const supplierName = setupLinkData?.supplier?.name || setupLinkData?.supplier?.company_name || "Supplier";
                const contactName = setupLinkData?.supplier?.contact_name || "";
                const email = setupLinkData?.supplier?.email || "";
                const setupLink = setupLinkData?.setup_link || "";
                
                const subject = encodeURIComponent(`Welcome to AskIsaac - Complete Your Supplier Account Setup`);
                const body = encodeURIComponent(
`Dear ${contactName || supplierName},

Welcome to the AskIsaac platform!

You have been invited to join AskIsaac as a verified supplier partner. To complete your account setup and start receiving quote requests, please click the link below:

${setupLink}

WHAT TO EXPECT:
• Set your secure password
• Review and accept our terms of service
• Connect your Telegram for real-time quote alerts (@askisaac_bot)
• Start receiving RFQs from hospitals

This link will expire in 7 days. If you have any questions, please contact our support team.

We look forward to working with you!

Best regards,
The AskIsaac Team
Sterile Mate LLC
www.sterilemate.com
info@sterilemate.com
`
                );
                
                window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                toast.success("Opening email client...");
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Setup Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Supplier Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600" />
              Email Supplier
            </DialogTitle>
            <DialogDescription>
              Pre-fills your default email client (Outlook, Apple Mail, Gmail) with a message to {emailSupplier?.name || emailSupplier?.contact_name} ({emailSupplier?.email}). You hit Send from your own account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
                data-testid="email-subject"
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Enter your message..."
                rows={6}
                data-testid="email-body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEmailModal(false); setEmailSupplier(null); }}>Cancel</Button>
            <Button 
              onClick={handleSendEmail} 
              disabled={!emailSubject || !emailBody}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="open-supplier-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Open in My Outlook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Full Profile Modal — documents, certifications, two-step verify */}
      <Dialog open={showSupplierProfile} onOpenChange={setShowSupplierProfile}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" />
              Supplier Profile
            </DialogTitle>
            <DialogDescription>
              Review documents, certifications and approve the supplier for assignments.
            </DialogDescription>
          </DialogHeader>
          {supplierProfile && (
            <div className="space-y-5">
              {/* Header */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">{supplierProfile.name}</h3>
                    <p className="text-slate-500">{supplierProfile.contact_name}</p>
                  </div>
                  {supplierFullProfile?.verification?.is_verified ? (
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold inline-flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Verified
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold inline-flex items-center gap-1">
                      <ShieldAlert className="w-4 h-4" /> {supplierFullProfile?.verification?.verification_status === "pending_review" ? "Pending Review" : "Pending Documents"}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4" />{supplierProfile.email}</div>
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4" />{supplierProfile.phone || "N/A"}</div>
                  {(supplierProfile.city || supplierProfile.state) && (
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{supplierProfile.city}{supplierProfile.city && supplierProfile.state ? ', ' : ''}{supplierProfile.state}</div>
                  )}
                  {supplierProfile.website && (
                    <div className="flex items-center gap-2"><Globe className="w-4 h-4" />{supplierProfile.website}</div>
                  )}
                </div>
                {supplierProfile.categories?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {supplierProfile.categories.map((c) => (
                      <span key={c} className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-xs">{c.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Verification checklist */}
              <div data-testid="supplier-verification-section">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  Documents & Verification
                </h4>
                {supplierFullProfile?.verification?.verified_at && (
                  <p className="text-xs text-slate-500 mb-2">
                    Verified on {new Date(supplierFullProfile.verification.verified_at).toLocaleDateString()} by {supplierFullProfile.verification.verified_by || "admin"}
                  </p>
                )}
                <div className="space-y-2 mb-3">
                  {supplierProfileLoading && !supplierFullProfile?.checklist?.length && (
                    <div className="text-center py-3 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading documents…</div>
                  )}
                  {(supplierFullProfile?.checklist || []).map((item) => (
                    <div key={item.type} className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        {item.uploaded ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                        <span className="text-sm text-slate-700">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.uploaded ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {item.uploaded ? "Uploaded" : "Missing"}
                        </span>
                        <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 px-2 py-1 rounded border border-amber-200 hover:bg-amber-50" data-testid={`upload-supplier-${item.type}-btn`}>
                          <FileText className="w-3 h-3" />
                          {item.uploaded ? "Replace" : "Upload"}
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadSupplierDocument(supplierProfile.id, f, item.type);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Free-form upload — additional certifications */}
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <FileText className="w-4 h-4 text-amber-600" />
                    Add an additional certification or product brochure
                  </div>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded">
                    + Upload File
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const customName = window.prompt("Document name (e.g. 'ISO 9001 Cert')", f.name) || f.name;
                        const typeSlug = customName.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40) || "additional";
                        uploadSupplierDocument(supplierProfile.id, f, typeSlug, customName);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>

                {(supplierFullProfile?.documents?.length || 0) > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Uploaded files</p>
                    <div className="space-y-1">
                      {supplierFullProfile.documents.map((d) => (
                        <div key={d.id || d.document_id} className="flex items-center justify-between p-2 bg-amber-50 hover:bg-amber-100 rounded-lg text-sm">
                          <a href={d.file_url || d.url || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 flex-1 min-w-0">
                            <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                            <span className="truncate">{d.document_name || d.document_type || d.file_name || "Document"}</span>
                            {d.uploaded_by_role === "admin" && (
                              <span className="text-[10px] px-1.5 py-0 rounded bg-amber-200 text-amber-900">admin</span>
                            )}
                          </a>
                          <div className="flex items-center gap-1 shrink-0">
                            <a href={d.file_url || d.url || "#"} target="_blank" rel="noreferrer">
                              <Download className="w-4 h-4 text-amber-600" />
                            </a>
                            <button
                              onClick={() => deleteSupplierDocument(supplierProfile.id, d.id || d.document_id)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove document"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {supplierFullProfile?.verification?.is_verified ? (
                    <Button
                      variant="outline"
                      className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                      onClick={() => handleRevokeSupplierVerification(supplierProfile)}
                      disabled={verifyingSupplier}
                      data-testid="revoke-supplier-verification-btn"
                    >
                      <ShieldAlert className="w-4 h-4 mr-2" />
                      Revoke Verified Status
                    </Button>
                  ) : (
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleVerifySupplier(supplierProfile)}
                      disabled={verifyingSupplier || !supplierFullProfile?.ready_for_admin_verify}
                      data-testid="mark-supplier-verified-btn"
                      title={!supplierFullProfile?.ready_for_admin_verify ? "All required documents must be uploaded first" : "Mark supplier as fully verified"}
                    >
                      {verifyingSupplier ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Mark as Verified
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => openSupplierActivationEmail(supplierProfile)} title="Send activation email from your Outlook">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Activation Email
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSupplierProfile(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
// Supplier Table View Component
function SupplierTable({ suppliers, onGenerateLink, generatingLink, selectedSupplier, onEdit, onEmail, onToggleAlertCampaign, onViewProfile, onSendActivation, onViewLedger }) {
  if (suppliers.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>No suppliers found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Supplier</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Categories</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Location</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Alert Campaign</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {suppliers.map(supplier => (
            <tr key={supplier.id} className="hover:bg-slate-50" data-testid={`supplier-row-${supplier.id}`}>
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium text-slate-900">{supplier.name}</p>
                  {supplier.contact_name && (
                    <p className="text-sm text-slate-500">{supplier.contact_name}</p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <Mail className="w-3 h-3" />
                    {supplier.email}
                  </div>
                  {supplier.phone && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Phone className="w-3 h-3" />
                      {supplier.phone}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {supplier.categories?.slice(0, 2).map(cat => (
                    <span key={cat} className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-xs">{labelForCategory(cat)}</span>
                  ))}
                  {supplier.categories?.length > 2 && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">+{supplier.categories.length - 2}</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                {(supplier.city || supplier.state) ? (
                  <span className="text-sm text-slate-600">
                    {supplier.city}{supplier.city && supplier.state ? ', ' : ''}{supplier.state}
                  </span>
                ) : (
                  <span className="text-sm text-slate-400">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  {supplier.is_verified ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 inline-flex items-center gap-1 w-fit" data-testid={`verified-badge-${supplier.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1 w-fit">
                      <ShieldAlert className="w-3 h-3" /> Pending Verify
                    </span>
                  )}
                  {supplier.password_hash || supplier.setup_completed_at ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 inline-flex items-center w-fit">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 inline-flex items-center w-fit">
                      Not Activated
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-2">
                  <Switch
                    checked={supplier.alert_campaign_enabled ?? false}
                    onCheckedChange={(checked) => onToggleAlertCampaign(supplier, checked)}
                    data-testid={`alert-campaign-toggle-${supplier.id}`}
                  />
                  <Megaphone className={`w-4 h-4 ${supplier.alert_campaign_enabled ? 'text-amber-500' : 'text-slate-300'}`} />
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onViewProfile(supplier)} title="View Full Profile" data-testid={`view-supplier-profile-${supplier.id}`}>
                    <Eye className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onViewLedger(supplier)}
                    title="Payment & remit details · invoice ledger · printable statement"
                    data-testid={`supplier-ledger-btn-${supplier.id}`}>
                    <Banknote className="w-4 h-4 text-emerald-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEmail(supplier)}
                    title="Send Email"
                    data-testid={`email-supplier-${supplier.id}`}
                  >
                    <MessageSquare className="w-4 h-4 text-amber-500" />
                  </Button>
                  {!supplier.password_hash && !supplier.setup_completed_at && (
                    <Button variant="ghost" size="sm" onClick={() => onSendActivation(supplier)} title="Send Activation Email via Outlook" data-testid={`send-supplier-activation-${supplier.id}`}>
                      <Mail className="w-4 h-4 text-emerald-600" />
                    </Button>
                  )}
                  {!supplier.password_hash && !supplier.setup_completed_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onGenerateLink(supplier)}
                      title="Generate Setup Link"
                      disabled={generatingLink && selectedSupplier?.id === supplier.id}
                    >
                      <Link className="w-4 h-4 text-emerald-500" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => onEdit(supplier)} title="Edit">
                    <Edit className="w-4 h-4 text-slate-500" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Supplier Card Grid Component
function SupplierGrid({ suppliers, onGenerateLink, generatingLink, selectedSupplier, onEdit, onEmail, onToggleAlertCampaign, onViewProfile, onSendActivation, onViewLedger }) {
  if (suppliers.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>No suppliers found</p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {suppliers.map(supplier => (
        <div 
          key={supplier.id} 
          className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow"
          data-testid={`supplier-${supplier.id}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{supplier.name}</h3>
                {supplier.contact_name && (
                  <p className="text-sm text-slate-500">{supplier.contact_name}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {supplier.is_verified ? (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" /> Pending Verify
                </span>
              )}
              {supplier.password_hash || supplier.setup_completed_at ? (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                  Active
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                  Not Activated
                </span>
              )}
            </div>
          </div>
          
          <div className="space-y-2 text-sm mb-4">
            <div className="flex items-center gap-2 text-slate-600">
              <Mail className="w-4 h-4 text-slate-400" />
              {supplier.email}
            </div>
            {supplier.phone && (
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4 text-slate-400" />
                {supplier.phone}
              </div>
            )}
            {(supplier.city || supplier.state) && (
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400" />
                {supplier.city}{supplier.city && supplier.state ? ', ' : ''}{supplier.state}
              </div>
            )}
            {supplier.website && (
              <div className="flex items-center gap-2 text-slate-600">
                <Globe className="w-4 h-4 text-slate-400" />
                <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                  {supplier.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
            {supplier.credentials && (
              <div className="flex items-start gap-2 text-slate-600">
                <Key className="w-4 h-4 text-slate-400 mt-0.5" />
                <span className="text-xs bg-slate-100 px-2 py-1 rounded font-mono truncate max-w-[200px]" title={String(supplier.credentials)}>
                  {String(supplier.credentials).substring(0, 30)}{String(supplier.credentials).length > 30 ? '...' : ''}
                </span>
              </div>
            )}
          </div>
          
          {/* Categories */}
          {supplier.categories?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {supplier.categories.slice(0, 3).map(cat => (
                <span key={cat} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                  {labelForCategory(cat)}
                </span>
              ))}
              {supplier.categories.length > 3 && (
                <span className="px-2 py-0.5 bg-slate-100 text-slate-400 rounded text-xs">
                  +{supplier.categories.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Alert Campaign Permission Toggle */}
          <div className="flex items-center justify-between py-3 px-3 mb-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2">
              <Megaphone className={`w-4 h-4 ${supplier.alert_campaign_enabled ? 'text-amber-500' : 'text-slate-400'}`} />
              <span className="text-sm font-medium text-slate-700">Alert Campaign</span>
            </div>
            <Switch
              checked={supplier.alert_campaign_enabled ?? false}
              onCheckedChange={(checked) => onToggleAlertCampaign(supplier, checked)}
              data-testid={`alert-campaign-toggle-grid-${supplier.id}`}
            />
          </div>
          
          {/* Action Buttons */}
          <div className="pt-3 border-t border-slate-100 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewProfile(supplier)}
              className="flex-1"
              data-testid={`view-supplier-profile-grid-${supplier.id}`}
            >
              <Eye className="w-4 h-4 mr-1" />
              Profile
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewLedger(supplier)}
              title="Payables & invoices · payment and remit details"
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              data-testid={`supplier-ledger-btn-grid-${supplier.id}`}
            >
              <Banknote className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onEmail(supplier)}
              className="text-amber-600 border-amber-200 hover:bg-amber-50"
              data-testid={`email-supplier-grid-${supplier.id}`}
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
            {!supplier.password_hash && !supplier.setup_completed_at && (
              <Button
                size="sm"
                variant="outline"
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => onSendActivation(supplier)}
                title="Send Activation Email via Outlook"
              >
                <Mail className="w-4 h-4" />
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onEdit(supplier)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            {!supplier.password_hash && !supplier.setup_completed_at && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => onGenerateLink(supplier)}
                disabled={generatingLink && selectedSupplier?.id === supplier.id}
                title="Generate Setup Link"
              >
                {generatingLink && selectedSupplier?.id === supplier.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Link className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
